import { Component, ChangeDetectionStrategy } from '@angular/core';
import {
    ColDef,
    GridOptions,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { MultiSelectFilterComponent } from '../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import { UserRoleLabelMapping } from '../../../core/enums/users/role';
import { UserManagementService } from '../shared/services/user-management/user-management.service';
import { ActionsCellRendererComponent } from '../../../shared/components/grid/cell-renderers/users-management/actions-cell-renderer/actions-cell-renderer.component';
import { ActionsCellRendererParams } from '../../../core/models/grid/actions-cell-renderer-params';
import { AgGridAngular } from 'ag-grid-angular';
import { GridUser } from '../shared/models/grid-user';
import { UserStatus } from '../../../core/models/user-status';
import { User } from '../shared/models/user';
import { GridHelper } from '../../../core/models/grid/grid-helper';
import { IGridComponent } from '../../../core/models/grid/grid-component';
import { UserMembershipStatusActiveLabels } from '../../../core/enums/users/membership-status';
import { GridUserRowData } from './models/grid-user-row-data';
import { GridToolbarComponent } from '../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { nameOf } from '../../../core/functions/string-helpers';
import { ActionsCellRendererValue } from '../../../core/models/grid/actions-cell-renderer-value';
import { UserInformationService } from '../shared/services/user-information/user-information.service';
import { UserRoutes } from '../../../core/constants/routes';

@Component({
    selector: 'app-user-management',
    imports: [AgGridAngular, GridToolbarComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './user-management.component.html',
})
export class UserManagementComponent implements IGridComponent {
    rowData: GridUserRowData[] = [];
    gridApi: GridApi | undefined;
    context: unknown;

    columnDefs: ColDef[] = [
        {
            field: 'lastName',
            width: 150,
        },
        { field: 'firstName', width: 130 },
        { field: 'username', width: 120 },
        { field: 'email', width: 220, minWidth: 180 },
        {
            field: 'role',
            filter: MultiSelectFilterComponent,
            filterParams: {
                filterOptions: Object.values(UserRoleLabelMapping)
                    .map((role) => ({
                        label: role,
                        value: role,
                        selected: false,
                    }))
                    .concat({ label: 'None', value: 'None', selected: false }),
            },
            width: 120,
        },
        {
            field: 'membership',
            filter: MultiSelectFilterComponent,
            filterParams: {
                filterOptions: Object.values(
                    UserMembershipStatusActiveLabels,
                ).map((status) => ({
                    label: status,
                    value: status,
                    selected: false,
                })),
            },
            width: 150,
            minWidth: 150,
        },
        {
            field: 'status',
            filter: MultiSelectFilterComponent,
            filterParams: {
                filterOptions: UserStatus.getStatuses().map((status) => ({
                    label: status,
                    value: status,
                    selected: false,
                })),
            },
            width: 120,
            sort: 'asc',
            // Sort pending users first
            comparator: (valueA: string, valueB: string) => {
                if (
                    valueA === UserStatus.PENDING &&
                    valueB !== UserStatus.PENDING
                ) {
                    return -1;
                }
                if (
                    valueA !== UserStatus.PENDING &&
                    valueB === UserStatus.PENDING
                ) {
                    return 1;
                }
                return valueA.localeCompare(valueB);
            },
            initialSortIndex: 0,
        },
        {
            field: 'lastLogin',
            ...GridHelper.defaultDateColDef,
            filter: false,
            width: 120,
            sort: 'desc',
            initialSortIndex: 1,
        },
        {
            field: 'actions',
            filter: false,
            sortable: false,
            width: 100,
            pinned: 'right',
            cellRenderer: ActionsCellRendererComponent,
            cellRendererParams: {
                editParams: {
                    path: UserRoutes.getFullRoute('EDIT'),
                },
                viewParams: {
                    path: '/users',
                },
                reviewParams: {
                    path: UserRoutes.getFullRoute('REVIEW'),
                },
            } as ActionsCellRendererParams,
        },
    ];

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        columnDefs: this.columnDefs,
    };

    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        exportCsvOptions: {
            columnKeys: [
                nameOf<GridUserRowData>('lastName'),
                nameOf<GridUserRowData>('firstName'),
                nameOf<GridUserRowData>('username'),
                nameOf<GridUserRowData>('email'),
                nameOf<GridUserRowData>('role'),
                nameOf<GridUserRowData>('membership'),
                nameOf<GridUserRowData>('status'),
                nameOf<GridUserRowData>('lastLogin'),
            ],
            fileName: GridHelper.getExportFileName('Users'),
        },
    };

    constructor(
        private readonly userManagementService: UserManagementService,
        private readonly userInformationService: UserInformationService,
    ) {}

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;

        this.userManagementService
            .getAll()
            .then((users: GridUser[]) => {
                this.rowData = this.transformUserData(users);
                if (this.gridApi) {
                    this.gridApi.setGridOption('rowData', this.rowData);
                }
            })
            .catch((error: Error) => {
                console.error('Error fetching users', error);
            });
    }

    private transformUserData(users: GridUser[]): GridUserRowData[] {
        return users.map((gridUserObject: GridUser) => ({
            firstName: this.userInformationService.getFirstName(gridUserObject),
            lastName: this.userInformationService.getLastName(gridUserObject),
            username: gridUserObject.user.username,
            email: gridUserObject.user.email,
            membership: this.userInformationService.getMembershipStatusLabel(
                gridUserObject.userExtension?.membershipStatus,
            ),
            role: this.userInformationService.getRoleLabel(gridUserObject.user),
            status: UserStatus.getUserStatus(gridUserObject.user),
            lastLogin: this.userInformationService.getLastLoginDate(
                gridUserObject.user,
            ),
            actions: this.getActionsCellRendererValue(gridUserObject.user),
        }));
    }

    private getActionsCellRendererValue(user: User): ActionsCellRendererValue {
        if (
            UserStatus.permittedUserStatusesForUserReview.includes(
                UserStatus.getUserStatus(user),
            )
        ) {
            return {
                review: { id: user.id },
            };
        } else if (
            UserStatus.getUserStatus(user) === UserStatus.ACTIVE ||
            UserStatus.getUserStatus(user) == UserStatus.INACTIVE
        ) {
            return {
                edit: { id: user.id },
            };
        } else {
            return {
                view: { id: user.id },
            };
        }
    }
}
